import { Icono } from '@xiltrion/ui/icono';
import { Checkbox } from '@xiltrion/ui/checkbox';
import { Field as FieldUi, FieldContent, FieldDescription, FieldLabel, FieldTitle } from '@xiltrion/ui/field';
import { Input } from '@xiltrion/ui/input';
import { Label } from '@xiltrion/ui/label';
import { RadioGroup, RadioGroupItem } from '@xiltrion/ui/radio-group';
import { Field } from './shared';

/** Piezas comunes a las tarjetas de correo saliente y de buzón entrante. */

export const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

export interface ProviderOption<T extends string> {
  value: T;
  label: string;
  icon: `${string}:${string}`;
  description: string;
}

/** Tarjetas de opción para elegir el servicio. */
export function ProviderRadio<T extends string>({ value, options, onChange, idPrefix, ariaLabel }: { value: T; options: Array<ProviderOption<T>>; onChange: (v: T) => void; idPrefix: string; ariaLabel: string }) {
  return (
    <RadioGroup value={value} onValueChange={(p) => onChange(p as T)} className="grid-cols-1 md:grid-cols-3 gap-2" aria-label={ariaLabel}>
      {options.map((p) => (
        <FieldLabel key={p.value} htmlFor={`${idPrefix}-${p.value}`} className="cursor-pointer">
          <FieldUi orientation="horizontal">
            <FieldContent><Icono nombre={p.icon} className="text-xl text-primary" aria-hidden /><FieldTitle>{p.label}</FieldTitle><FieldDescription className="text-xs">{p.description}</FieldDescription></FieldContent>
            <RadioGroupItem id={`${idPrefix}-${p.value}`} value={p.value} />
          </FieldUi>
        </FieldLabel>
      ))}
    </RadioGroup>
  );
}

/** Contraseña, secreto del cliente o llave: nunca se muestra; vacío conserva el guardado. */
export function SecretField({ id, label, value, onChange, stored, clearable, clear, onClear }: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  /** Hay uno guardado que aplica a lo que está en el formulario. */
  stored: boolean;
  /** Ofrecer «quitar el guardado» (solo donde el secreto es opcional). */
  clearable?: boolean;
  clear?: boolean;
  onClear?: (c: boolean) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Field label={label} htmlFor={id} hint={stored ? 'Hay uno guardado; escriba otro solo para cambiarlo.' : undefined}>
        <Input id={id} type="password" autoComplete="new-password" placeholder={stored ? '••••••••' : ''} value={value} disabled={!!clear} onChange={(e) => onChange(e.target.value)} />
      </Field>
      {stored && clearable && <div className="flex items-center gap-2"><Checkbox id={`${id}-clear`} checked={!!clear} onCheckedChange={(c) => onClear?.(!!c)} /><Label htmlFor={`${id}-clear`} className="font-normal">Quitar la contraseña guardada</Label></div>}
    </div>
  );
}

/** Del JSON de la cuenta de servicio de Google salen el correo de la cuenta y la llave; el archivo no se guarda. */
export async function readServiceAccountJson(file: File): Promise<{ clientEmail: string; privateKey: string }> {
  const j = JSON.parse(await file.text()) as { type?: string; client_email?: string; private_key?: string };
  if (j.type !== 'service_account' || !j.client_email || !j.private_key) throw new Error('No es el JSON de una cuenta de servicio de Google (debe traer client_email y private_key).');
  return { clientEmail: j.client_email, privateKey: j.private_key };
}
